import React, { useEffect, useState } from "react";
import useCustomers from "../../hooks/useCustomers";
import { CustomerPDF, CustomerPDFDownload } from "./CustomerPDF";
import { pdf } from "@react-pdf/renderer";
import { toast } from "react-toastify";

import Header from "../../components/Header/header";
import ConfirmModal from "../../components/ConfirmModal/confirmModal";
import DispatchModal from "../../components/DispatchModal/DispatchModal"; // ✅ Import DispatchModal
import {
  FaWhatsapp,
  FaPhone,
  FaEye,
  FaEyeSlash,
  FaFilePdf,
} from "react-icons/fa";
import "./customersPage.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// ✅ Detect if user is on mobile
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// ✅ Inline PDF preview
function PdfPreview({ customer }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    const generatePdf = async () => {
      const blob = await pdf(<CustomerPDF customer={customer} />).toBlob();
      setBlobUrl(URL.createObjectURL(blob));
    };
    generatePdf();

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [customer]);

  if (!blobUrl) return <p>Loading PDF preview...</p>;

  return (
    <iframe
      src={blobUrl}
      width="100%"
      height="400"
      style={{ border: "none" }}
      title="Order PDF"
    />
  );
}

export default function CustomersPage() {
  const { customers, loading, error, fetchAll, deleteDispatched } =
    useCustomers();
  const [expandedRows, setExpandedRows] = useState({});
  const [statusMap, setStatusMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [modalAction, setModalAction] = useState(null);

  const pendingCount = customers.filter(
    (c) => statusMap[c._id] === "pending"
  ).length;

  // Sync statusMap with customers
  useEffect(() => {
    if (customers.length > 0) {
      const initialStatuses = {};
      customers.forEach((c) => {
        initialStatuses[c._id] = c.status || "pending";
      });
      setStatusMap(initialStatuses);
    }
  }, [customers]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // ✅ Dispatch / Mark Pending
  // const handleDispatch = async (id, formData, newStatus) => {
  //   try {
  //     const body =
  //       newStatus === "dispatched"
  //         ? {
  //             status: "dispatched",
  //             transportName: formData?.transportName,
  //             dispatchDate: formData?.dispatchDate,
  //             llrNumber: formData?.llrNumber,
  //           }
  //         : {
  //             status: "pending",
  //             transportName: "",
  //             dispatchDate: "",
  //             llrNumber: "",
  //           };

  //     const res = await fetch(`${API_BASE}/orders/${id}/status`, {
  //       method: "PATCH",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(body),
  //     });

  //     if (!res.ok) throw new Error("Failed to update status");

  //     setStatusMap((prev) => ({
  //       ...prev,
  //       [id]: newStatus,
  //     }));

  //     toast.success(
  //       newStatus === "dispatched"
  //         ? "Order marked as Dispatched ✅"
  //         : "Order marked as Pending ⚠️"
  //     );
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Error updating dispatch status ❌");
  //   }
  // };

  // ✅ Dispatch / Mark Pending
  const handleDispatch = async (id, formData, newStatus) => {
    try {
      const body =
        newStatus === "dispatched"
          ? {
              status: "dispatched",
              transportName: formData?.transportName,
              dispatchDate: formData?.dispatchDate,
              llrNumber: formData?.llrNumber,
            }
          : {
              status: "pending",
              transportName: "",
              dispatchDate: "",
              llrNumber: "",
            };

      const res = await fetch(`${API_BASE}/orders/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Failed to update status");

      // ✅ Refresh customers list from backend
      await fetchAll();

      toast.success(
        newStatus === "dispatched"
          ? "Order marked as Dispatched ✅"
          : "Order marked as Pending ⚠️"
      );
    } catch (err) {
      console.error(err);
      toast.error("Error updating dispatch status ❌");
    }
  };

  // ✅ Open Dispatch modal only when marking as Dispatched
  const confirmDispatch = (id) => {
    const currentStatus = statusMap[id] || "pending";

    if (currentStatus === "pending") {
      // Show Dispatch modal (transport details required)
      setSelectedId(id);
      setModalAction("dispatch");
      setModalOpen(true);
    } else {
      // Show ConfirmModal for marking pending
      setSelectedId(id);
      setModalAction("mark-pending");
      setModalOpen(true);
    }
  };

  // ✅ Open Delete modal
  const confirmDeleteDispatched = () => {
    setModalAction("delete");
    setModalOpen(true);
  };

  // ✅ Cancel action
  const handleCancel = () => {
    setModalOpen(false);
    setSelectedId(null);
    setModalAction(null);
  };

  // ✅ Open PDF in new tab for mobile
  const openPdfInNewTab = async (customer) => {
    const blob = await pdf(<CustomerPDF customer={customer} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  if (loading) return <p className="status-message">Loading customers...</p>;
  if (error) return <p className="status-message error">Error: {error}</p>;
  if (customers.length === 0)
    return <p className="status-message">No customers found.</p>;

  return (
    <div className="customers-container">
      <Header
        title="Customers"
        onRefresh={fetchAll}
        newCustomerCount={pendingCount}
      />

      <div className="table-wrapper">
        <table className="customers-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Address</th>
              <th>State</th>
              <th>Total</th>
              <th>Status</th>
              {/* <th>Action</th> */}
            </tr>
          </thead>

          {/* <tbody>
            {customers.map((c) => (
              <React.Fragment key={c._id}>
                <tr className="table-row">
                  <td>{c.customerName}</td>
                  <td>{c.customerNumber}</td>
                  <td>{c.customerAddress}</td>
                  <td>{c.customerState}</td>
                  <td className="bold">₹{c.totalRate.toFixed(2)}</td>
                  <td>
                    <span
                      className={
                        statusMap[c._id] === "dispatched"
                          ? "status dispatched"
                          : "status pending"
                      }
                    >
                      {statusMap[c._id] === "dispatched"
                        ? "Dispatched"
                        : "Pending"}
                    </span>
                    <button
                      className="dispatch-button"
                      onClick={() => confirmDispatch(c._id)}
                    >
                      {statusMap[c._id] === "dispatched"
                        ? "Mark Pending"
                        : "Dispatch"}
                    </button>
                  </td>

                  <td className="action-cell">
                    <button
                      onClick={() => toggleRow(c._id)}
                      className="view-button"
                    >
                      {expandedRows[c._id] ? <FaEyeSlash /> : <FaEye />}
                    </button>

                    <button
                      onClick={() =>
                        window.open(
                          `https://wa.me/${c.customerNumber}`,
                          "_blank"
                        )
                      }
                      className="whatsapp-button"
                    >
                      <FaWhatsapp />
                    </button>

                    <button
                      onClick={() =>
                        (window.location.href = `tel:${c.customerNumber}`)
                      }
                      className="call-button"
                    >
                      <FaPhone />
                    </button>
                  </td>
                </tr>

                {expandedRows[c._id] && (
                  <tr className="expanded-row">
                    <td colSpan="7">
                      <CustomerPDFDownload customer={c}>
                        <FaFilePdf /> Download
                      </CustomerPDFDownload>

                      {!isMobile ? (
                        <div
                          style={{
                            marginTop: "10px",
                            border: "1px solid #ccc",
                          }}
                        >
                          <PdfPreview customer={c} />
                        </div>
                      ) : (
                        <button
                          onClick={() => openPdfInNewTab(c)}
                          style={{
                            marginTop: "10px",
                            padding: "8px 12px",
                            background: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            marginLeft: "20px",
                          }}
                        >
                          Open PDF
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody> */}

          <tbody>
            {customers.map((c) => (
              <React.Fragment key={c._id}>
                {/* Main row */}
                <tr className="table-row">
                  <td>{c.customerName}</td>
                  <td>{c.customerNumber}</td>
                  <td>{c.customerAddress}</td>
                  <td>{c.customerState}</td>
                  <td className="bold">₹{c.totalRate.toFixed(2)}</td>
                  <td className="status-cell">
                    <span
                      className={
                        statusMap[c._id] === "dispatched"
                          ? "status dispatched"
                          : "status pending"
                      }
                    >
                      {statusMap[c._id] === "dispatched"
                        ? "Dispatched"
                        : "Pending"}
                    </span>
                    <button
                      className="dispatch-button"
                      onClick={() => confirmDispatch(c._id)}
                    >
                      {statusMap[c._id] === "dispatched"
                        ? "Mark Pending"
                        : "Dispatch"}
                    </button>
                  </td>

                  <td className="action-cell">
                    <button
                      onClick={() => toggleRow(c._id)}
                      className="view-button"
                    >
                      {expandedRows[c._id] ? <FaEyeSlash /> : <FaEye />}
                    </button>

                    <button
                      onClick={() =>
                        window.open(
                          `https://wa.me/${c.customerNumber}`,
                          "_blank"
                        )
                      }
                      className="whatsapp-button"
                    >
                      <FaWhatsapp />
                    </button>

                    <button
                      onClick={() =>
                        (window.location.href = `tel:${c.customerNumber}`)
                      }
                      className="call-button"
                    >
                      <FaPhone />
                    </button>
                  </td>
                </tr>

                {/* Transport details row (only if dispatched) */}
                {statusMap[c._id] === "dispatched" && (
                  <tr className="transport-row">
                    <td colSpan="7">
                      <div
                        style={{
                          display: "flex",
                          gap: "30px",
                          padding: "6px 12px",
                          justifyContent: "space-around",
                        }}
                      >
                        <div>
                          <strong>🚚Transport:</strong>
                          {c.transportName || "-"}
                        </div>
                        <div>
                          <strong>📅Date:</strong>{" "}
                          {c.dispatchDate
                            ? new Date(c.dispatchDate).toLocaleDateString()
                            : "-"}
                        </div>
                        <div>
                          <strong>📦LLR No:</strong> {c.llrNumber || "-"}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Expanded row for PDF preview */}
                {expandedRows[c._id] && (
                  <tr className="expanded-row">
                    <td colSpan="7">
                      <CustomerPDFDownload customer={c}>
                        <FaFilePdf /> Download
                      </CustomerPDFDownload>

                      {!isMobile ? (
                        <div
                          style={{
                            marginTop: "10px",
                            border: "1px solid #ccc",
                          }}
                        >
                          <PdfPreview customer={c} />
                        </div>
                      ) : (
                        <button
                          onClick={() => openPdfInNewTab(c)}
                          style={{
                            marginTop: "10px",
                            padding: "8px 12px",
                            background: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            marginLeft: "20px",
                          }}
                        >
                          Open PDF
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <button className="delete-button" onClick={confirmDeleteDispatched}>
            Delete All Dispatched Customers
          </button>
        </div>
      </div>

      {/* ✅ Show modal depending on action */}
      {modalAction === "dispatch" ? (
        <DispatchModal
          open={modalOpen}
          onClose={handleCancel}
          onSubmit={(formData) => {
            handleDispatch(selectedId, formData, "dispatched");
            handleCancel();
          }}
        />
      ) : modalAction === "mark-pending" ? (
        <ConfirmModal
          open={modalOpen}
          message="Are you sure you want to mark this order as Pending?"
          onConfirm={() => {
            handleDispatch(selectedId, {}, "pending");
            handleCancel();
          }}
          onCancel={handleCancel}
        />
      ) : (
        <ConfirmModal
          open={modalOpen}
          message="Are you sure you want to delete ALL dispatched customers?"
          onConfirm={() => {
            deleteDispatched();
            toast.success("All dispatched customers deleted 🗑️");
            handleCancel();
          }}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}
